import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  Ticket, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Calendar,
  Plus,
  Star,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import SupportTicketForm from './SupportTicketForm';

interface SupportTicket {
  id: string;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface TicketResponse {
  id: string;
  response_text: string;
  created_at: string;
  admin_name?: string;
  is_admin_response: boolean;
  responded_by: string;
}

interface TicketRating {
  id: string;
  rating: number;
  feedback: string | null;
  created_at: string;
}

interface UserSupportTicketsProps {
  onBack?: () => void;
}

const UserSupportTickets: React.FC<UserSupportTicketsProps> = ({ onBack }) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const currentUser = supabaseUser || internalUser;
  
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [responses, setResponses] = useState<TicketResponse[]>([]);
  const [ticketRating, setTicketRating] = useState<TicketRating | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      fetchMyTickets();
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (selectedTicket) {
      fetchTicketResponses(selectedTicket.id);
      fetchTicketRating(selectedTicket.id);
    }
  }, [selectedTicket]);

  const fetchMyTickets = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load your tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTicketResponses = async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('support_ticket_responses')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Get admin info separately
      const adminIds = [...new Set(data?.map(response => response.responded_by).filter(Boolean) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', adminIds);

      const responsesWithAdminInfo = data?.map(response => {
        const profile = profiles?.find(p => p.id === response.responded_by);
        return {
          ...response,
          admin_name: response.is_admin_response 
            ? (profile?.username || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Support Team')
            : (profile?.username || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'User')
        };
      }) || [];

      setResponses(responsesWithAdminInfo);
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to load ticket responses');
    }
  };

  const fetchTicketRating = async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('support_ticket_ratings')
        .select('*')
        .eq('ticket_id', ticketId)
        .eq('user_id', currentUser?.id)
        .maybeSingle();

      if (error) throw error;
      setTicketRating(data);
    } catch (error) {
      console.error('Error fetching ticket rating:', error);
    }
  };

  const submitRating = async () => {
    if (!selectedTicket || !currentUser?.id || rating === 0) return;

    try {
      const { error } = await supabase
        .from('support_ticket_ratings')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: currentUser.id,
          rating,
          feedback: feedback.trim() || null
        });

      if (error) throw error;

      toast.success('Thank you for your feedback!');
      setShowRatingForm(false);
      setRating(0);
      setFeedback('');
      fetchTicketRating(selectedTicket.id);
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    }
  };

  const submitResponse = async () => {
    if (!selectedTicket || !currentUser?.id || !newResponse.trim()) return;

    try {
      setIsSubmittingResponse(true);
      const { error } = await supabase
        .from('support_ticket_responses')
        .insert({
          ticket_id: selectedTicket.id,
          response_text: newResponse.trim(),
          responded_by: currentUser.id,
          is_admin_response: false
        });

      if (error) throw error;

      toast.success('Response added successfully!');
      setNewResponse('');
      fetchTicketResponses(selectedTicket.id);
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to submit response');
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-destructive';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-primary';
      case 'low': return 'bg-muted-foreground';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-primary';
      case 'closed': return 'bg-muted-foreground';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <AlertCircle className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      case 'closed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (!currentUser) {
    return (
      <Card className="bg-background border-border max-w-md mx-auto">
        <CardContent className="p-6 text-center">
          <h2 className="text-xl font-bold text-foreground mb-2">Authentication Required</h2>
          <p className="text-muted-foreground">Please log in to view your support tickets.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="text-foreground">Loading your tickets...</div>
      </div>
    );
  }

  if (showNewTicketForm) {
    return (
      <div className="space-y-4">
        {onBack && (
          <Button
            onClick={onBack}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
        )}
        <SupportTicketForm onClose={() => {
          setShowNewTicketForm(false);
          fetchMyTickets();
        }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button
              onClick={onBack}
              variant="outline"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Ticket className="h-6 w-6 text-primary" />
            My Support Tickets
          </h2>
        </div>
        <Button
          onClick={() => setShowNewTicketForm(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Ticket
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tickets List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-card-foreground">Your Tickets ({tickets.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedTicket?.id === ticket.id
                    ? 'border-primary bg-muted'
                    : 'border-border bg-card hover:bg-muted'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-medium text-card-foreground">{ticket.ticket_number}</div>
                    <div className="text-sm text-muted-foreground">{ticket.subject}</div>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={`${getPriorityColor(ticket.priority)} text-primary-foreground`}>
                      {ticket.priority}
                    </Badge>
                    <Badge className={`${getStatusColor(ticket.status)} text-primary-foreground flex items-center gap-1`}>
                      {getStatusIcon(ticket.status)}
                      {ticket.status}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="capitalize">{ticket.category.replace('_', ' ')}</div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))
            }
            {tickets.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="mb-4">You haven't submitted any support tickets yet.</p>
                <Button
                  onClick={() => setShowNewTicketForm(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Submit Your First Ticket
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket Details */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-card-foreground flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {selectedTicket ? `Ticket Details` : 'Select a Ticket'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedTicket ? (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">{selectedTicket.subject}</h3>
                <div className="text-muted-foreground">
                  <strong>Ticket Number:</strong> {selectedTicket.ticket_number}
                </div>
                <div className="text-muted-foreground">
                  <strong>Category:</strong> {selectedTicket.category.replace('_', ' ')}
                </div>
                <div className="text-muted-foreground">
                  <strong>Priority:</strong> {selectedTicket.priority}
                </div>
                <div className="text-muted-foreground">
                  <strong>Status:</strong> {selectedTicket.status}
                </div>
                <div className="text-muted-foreground">
                  <strong>Created At:</strong> {new Date(selectedTicket.created_at).toLocaleDateString()}
                </div>
                <div className="text-muted-foreground">
                  <strong>Description:</strong>
                  <p className="mt-2">{selectedTicket.description}</p>
                </div>

                {/* Responses Section */}
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-foreground mb-2">Responses:</h4>
                  {responses.length > 0 ? (
                    <div className="space-y-3">
                       {responses.map((response) => (
                         <div key={response.id} className={`rounded-lg p-3 ${
                           response.is_admin_response 
                             ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' 
                             : 'bg-muted'
                         }`}>
                           <div className="flex items-center gap-2 mb-1">
                             <div className="text-sm text-foreground font-medium">
                               {response.admin_name || 'Support Team'}
                             </div>
                             {response.is_admin_response && (
                               <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Support</span>
                             )}
                             {!response.is_admin_response && response.responded_by === currentUser?.id && (
                               <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">You</span>
                             )}
                           </div>
                           <div className="text-sm text-foreground">{response.response_text}</div>
                           <div className="text-xs text-gray-400 mt-1">
                             {new Date(response.created_at).toLocaleDateString()}
                           </div>
                         </div>
                       ))}
                     </div>
                   ) : (
                     <div className="text-muted-foreground">No responses yet.</div>
                   )}
                 </div>

                 {/* User Response Form for Open Tickets */}
                 {selectedTicket.status !== 'closed' && (
                   <div className="space-y-3">
                     <h4 className="text-md font-semibold text-foreground">Add Response:</h4>
                     <Textarea
                       value={newResponse}
                       onChange={(e) => setNewResponse(e.target.value)}
                       placeholder="Add additional information or clarify your issue..."
                       className="bg-background border-border text-foreground"
                       rows={3}
                       disabled={isSubmittingResponse}
                     />
                     <div className="flex gap-2">
                       <Button
                         onClick={submitResponse}
                         disabled={!newResponse.trim() || isSubmittingResponse}
                         className="bg-primary text-primary-foreground hover:bg-primary/90"
                         size="sm"
                       >
                         {isSubmittingResponse ? 'Submitting...' : 'Add Response'}
                       </Button>
                       <Button
                         onClick={() => setNewResponse('')}
                         variant="outline"
                         size="sm"
                         disabled={isSubmittingResponse}
                       >
                         Clear
                       </Button>
                     </div>
                   </div>
                 )}
                {/* Rating Section for Resolved/Closed Tickets */}
                {(selectedTicket.status === 'resolved' || selectedTicket.status === 'closed') && (
                  <div className="space-y-3">
                    {ticketRating ? (
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                        <h4 className="text-green-700 dark:text-green-300 font-medium mb-2 flex items-center gap-2">
                          <Star className="h-4 w-4" />
                          Your Rating
                        </h4>
                        <div className="flex items-center gap-2 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-4 w-4 ${
                                star <= ticketRating.rating
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-muted-foreground'
                              }`}
                            />
                          ))}
                          <span className="text-green-700 dark:text-green-300 text-sm">
                            ({ticketRating.rating}/5 stars)
                          </span>
                        </div>
                        {ticketRating.feedback && (
                          <p className="text-sm text-foreground">{ticketRating.feedback}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Rated on {new Date(ticketRating.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                        <h4 className="text-blue-700 dark:text-blue-300 font-medium mb-2">Rate Our Support</h4>
                        <p className="text-sm text-foreground mb-3">
                          How would you rate the support you received for this ticket?
                        </p>
                        {!showRatingForm ? (
                          <Button
                            onClick={() => setShowRatingForm(true)}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                            size="sm"
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Rate Support
                          </Button>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  onClick={() => setRating(star)}
                                  className="p-1 hover:scale-110 transition-transform"
                                >
                                  <Star
                                    className={`h-6 w-6 ${
                                      star <= rating
                                        ? 'text-yellow-400 fill-yellow-400'
                                        : 'text-muted-foreground hover:text-yellow-300'
                                    }`}
                                  />
                                </button>
                              ))}
                            </div>
                            <Textarea
                              value={feedback}
                              onChange={(e) => setFeedback(e.target.value)}
                              placeholder="Optional: Share your feedback about the support experience..."
                              className="bg-background border-border text-foreground"
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <Button
                                onClick={submitRating}
                                disabled={rating === 0}
                                className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                size="sm"
                              >
                                Submit Rating
                              </Button>
                              <Button
                                onClick={() => {
                                  setShowRatingForm(false);
                                  setRating(0);
                                  setFeedback('');
                                }}
                                variant="outline"
                                size="sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a ticket to view details and responses</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserSupportTickets;
